// Custom hook: useMissionPeriod
// Provides state management and business logic for mission search and updates.
import useSlideOver from 'components/SlideOver/useSlideOver';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ApiDatabase from 'server';
import UseContext from './useContext';
import Usefetchdata from './usefetchdata';

const FILTER_STORAGE_VERSION = 'mission_filters_v1';

const HARDCODED_FILTER_DEFAULTS = {
  assigned: null,
  sourcing: null,
  typeContract: [],
  departments: [],
  missionPeriod: ['done', 'inProgress', 'soon'],
  missionStatus: ['online', 'suspended'],
  employeesSearch: [],
};

const serializeFilters = ({
  isMissionSearchAssigned,
  isMissionSearchSourcing,
  typeContract,
  departments,
  missionPeriod,
  missionStatus,
  employeesSearch,
}) => ({
  assigned: isMissionSearchAssigned,
  sourcing: isMissionSearchSourcing,
  typeContract: typeContract.filter((t) => t.checked).map((t) => t.type).sort(),
  departments: departments.filter((d) => d.checked).map((d) => d.name).sort(),
  missionPeriod: missionPeriod.filter((p) => p.checked).map((p) => p.type).sort(),
  missionStatus: missionStatus.filter((s) => s.checked).map((s) => s.type).sort(),
  employeesSearch: employeesSearch.filter((e) => e.checked).map((e) => e._id).sort(),
});

const getFilterStorageKey = (userId) => `${FILTER_STORAGE_VERSION}_${userId || 'anonymous'}`;

const readSavedFilters = (userId) => {
  try {
    const raw = localStorage.getItem(getFilterStorageKey(userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const DEFAULTS_SNAPSHOT = JSON.stringify(HARDCODED_FILTER_DEFAULTS);

const FILTER_URL_KEYS = ['assigned', 'sourcing', 'departments', 'period', 'status', 'adminid'];

const hasFilterUrlParams = (urlParams) =>
  FILTER_URL_KEYS.some((key) => urlParams[key] !== null && urlParams[key] !== '');

const buildFilterSearchParams = ({
  isMissionSearchAssigned,
  isMissionSearchSourcing,
  departments,
  missionPeriod,
  missionStatus,
  employeesSearch,
  search,
  editMission,
  currentPage,
}) => {
  const params = new URLSearchParams();

  if (isMissionSearchAssigned !== null) {
    params.set('assigned', String(isMissionSearchAssigned));
  }
  if (isMissionSearchSourcing !== null) {
    params.set('sourcing', String(isMissionSearchSourcing));
  }

  const checkedDepts = departments.filter((d) => d.checked).map((d) => d.name);
  if (checkedDepts.length > 0) {
    params.set('departments', checkedDepts.join(','));
  }

  const checkedPeriods = missionPeriod.filter((p) => p.checked).map((p) => p.type);
  if (checkedPeriods.length > 0) {
    params.set('period', checkedPeriods.join(','));
  }

  const checkedStatuses = missionStatus.filter((s) => s.checked).map((s) => s.type);
  if (checkedStatuses.length > 0) {
    params.set('status', checkedStatuses.join(','));
  }

  const checkedEmployees = employeesSearch.filter((e) => e.checked).map((e) => e._id);
  if (checkedEmployees.length > 0) {
    params.set('adminid', checkedEmployees.join(','));
  }

  if (search) {
    params.set('search', search);
  }

  if (editMission) {
    params.set('mission', editMission);
  }

  if (currentPage > 1) {
    params.set('page', String(currentPage));
  }

  return params;
};

const toSavedSnapshot = (serialized) =>
  JSON.stringify({
    assigned: serialized.assigned,
    sourcing: serialized.sourcing,
    typeContract: [...serialized.typeContract].sort(),
    departments: [...serialized.departments].sort(),
    missionPeriod: [...serialized.missionPeriod].sort(),
    missionStatus: [...serialized.missionStatus].sort(),
    employeesSearch: [...serialized.employeesSearch].sort(),
  });

export default function useMissionPeriod() {
  // External context and dependencies
  const { objID, setObjID, searchMissionResult, setSearchMissionResult } =
    UseContext();
  const { toggleSlideOver } = useSlideOver();
  const { userData } = Usefetchdata();
  const token = localStorage.getItem('xsrfToken');
  const [searchParams, setSearchParams] = useSearchParams();

  const initialUrlParams = useRef({
    assigned: searchParams.get('assigned'),
    sourcing: searchParams.get('sourcing'),
    departments: searchParams.get('departments'),
    period: searchParams.get('period'),
    status: searchParams.get('status'),
    adminid: searchParams.get('adminid'),
    mission: searchParams.get('mission'),
    search: searchParams.get('search'),
    page: searchParams.get('page'),
  });
  const pendingMissionFromUrl = useRef(initialUrlParams.current.mission || null);
  const isInitialMount = useRef(true);
  const filterSourceRef = useRef(
    hasFilterUrlParams(initialUrlParams.current) ? 'url' : 'default'
  );
  const [filtersReady, setFiltersReady] = useState(() =>
    hasFilterUrlParams(initialUrlParams.current)
  );

  const parseThreeState = (value) => {
    if (value === 'true') return true;
    if (value === 'false') return false;
    return null;
  };

  // Local state definitions — initialized from URL params when present
  const [searchMissionResultWaiting, setSearchMissionResultWaiting] =
    useState(false);
  const [isMissionSearchAssigned, setIsMissionSearchAssigned] = useState(
    () => initialUrlParams.current.assigned !== null
      ? parseThreeState(initialUrlParams.current.assigned)
      : null
  );
  const [isMissionSearchSourcing, setIsMissionSearchSourcing] = useState(
    () => initialUrlParams.current.sourcing !== null
      ? parseThreeState(initialUrlParams.current.sourcing)
      : null
  );
  const [isMissionSearchSigned, setIsMissionSearchSigned] = useState(null);
  const [isMissionSearchSignatureSend, setIsMissionSearchSignatureSend] =
    useState(null);
  const [isInfoMissionWaiting, setIsInfoMissionWaiting] = useState(false);
  const [isInfoMissionLoadData, setIsInfoMissionLoadData] = useState(false);
  const [editMission, setEditMission] = useState(
    () => initialUrlParams.current.mission || false
  );
  const [currentPage, setCurrentPage] = useState(() => {
    const p = parseInt(initialUrlParams.current.page);
    return !isNaN(p) && p >= 1 ? p : 1;
  });
  const [totalPages, setTotalPages] = useState(0);
  const [limitElement] = useState(10);
  const [countPerPage, setCountPerPage] = useState(10);
  const [countTotal, setCountTotal] = useState(0);
  const [hoveredCompanyItems, setHoveredCompanyItems] = useState({});

  const [search, setSearch] = useState(
    () => initialUrlParams.current.search || ''
  );

  // Contract type filter options
  const [typeContract, setTypeContract] = useState([
    { type: 'sendToSign', name: 'Envoyé pour signature', checked: false },
    {
      type: 'toBeSentToSign',
      name: 'À envoyer pour signature',
      checked: false,
    },
    { type: 'signByCompany', name: 'Signé par l’entreprise', checked: false },
    {
      type: 'signByTempWorker',
      name: 'Signé par l’intérimaire',
      checked: false,
    },
    { type: 'notCreated', name: 'Contrat non généré', checked: false },
  ]);

  // Department filter options — loaded dynamically from API
  const [departments, setDepartments] = useState([]);

  // Mission period filter (temporal)
  const [missionPeriod, setMissionPeriod] = useState(() => {
    const periodParam = initialUrlParams.current.period;
    const urlPeriods = periodParam ? periodParam.split(',') : null;
    return [
      { type: 'done', name: 'Terminée', checked: urlPeriods ? urlPeriods.includes('done') : true },
      { type: 'inProgress', name: 'En cours', checked: urlPeriods ? urlPeriods.includes('inProgress') : true },
      { type: 'soon', name: 'À venir', checked: urlPeriods ? urlPeriods.includes('soon') : true },
    ];
  });

  // Mission status filter (action/visibility)
  const [missionStatus, setMissionStatus] = useState(() => {
    const statusParam = initialUrlParams.current.status;
    const urlStatuses = statusParam ? statusParam.split(',') : null;
    return [
      { type: 'online', name: 'En ligne', checked: urlStatuses ? urlStatuses.includes('online') : true },
      { type: 'suspended', name: 'Suspendue', checked: urlStatuses ? urlStatuses.includes('suspended') : true },
      { type: 'canceled', name: 'Annulée', checked: urlStatuses ? urlStatuses.includes('canceled') : false },
      { type: 'interrupted', name: 'Interrompue', checked: urlStatuses ? urlStatuses.includes('interrupted') : false },
    ];
  });

  const hasInitializedFilters = useRef(false);
  const savedDepartmentsRef = useRef([]);
  const savedEmployeesRef = useRef([]);

  // Snapshot of what's persisted in localStorage (JSON string for fast comparison)
  const [savedFiltersSnapshot, setSavedFiltersSnapshot] = useState(
    () => DEFAULTS_SNAPSHOT
  );
  const [hasSavedFilters, setHasSavedFilters] = useState(false);

  const [role, setRole] = useState(500);
  const [selectedMissions, setSelectedMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeesSearch, setEmployeesSearch] = useState([]);

  // Update role state from userData
  useEffect(() => {
    if (userData?.role) {
      setRole(userData.role);
    }
  }, [userData]);

  // Initialize filters once: URL params take priority, otherwise localStorage, then defaults
  useEffect(() => {
    if (hasInitializedFilters.current) return;
    if (!userData?._id) return;

    const missionId =
      initialUrlParams.current.mission || null;
    if (missionId) {
      pendingMissionFromUrl.current = missionId;
    }

    const useUrlFilters = hasFilterUrlParams(initialUrlParams.current);

    if (useUrlFilters) {
      filterSourceRef.current = 'url';
      const saved = readSavedFilters(userData._id);
      if (saved) {
        setHasSavedFilters(true);
        setSavedFiltersSnapshot(toSavedSnapshot(saved));
      }
      hasInitializedFilters.current = true;
      setFiltersReady(true);
      return;
    }

    const saved = readSavedFilters(userData._id);
    if (saved) {
      filterSourceRef.current = 'localStorage';
      savedDepartmentsRef.current = saved.departments || [];
      savedEmployeesRef.current = saved.employeesSearch || [];
      setHasSavedFilters(true);
      setSavedFiltersSnapshot(toSavedSnapshot(saved));

      if (saved.assigned !== undefined) setIsMissionSearchAssigned(saved.assigned);
      if (saved.sourcing !== undefined) setIsMissionSearchSourcing(saved.sourcing);

      setTypeContract((prev) =>
        prev.map((t) => ({
          ...t,
          checked: (saved.typeContract || []).includes(t.type),
        }))
      );
      setMissionPeriod((prev) =>
        prev.map((p) => ({
          ...p,
          checked: (saved.missionPeriod || HARDCODED_FILTER_DEFAULTS.missionPeriod).includes(p.type),
        }))
      );
      setMissionStatus((prev) =>
        prev.map((s) => ({
          ...s,
          checked: (saved.missionStatus || HARDCODED_FILTER_DEFAULTS.missionStatus).includes(s.type),
        }))
      );
    } else {
      filterSourceRef.current = 'default';
      setSavedFiltersSnapshot(DEFAULTS_SNAPSHOT);
      setHasSavedFilters(false);
    }

    hasInitializedFilters.current = true;
    setFiltersReady(true);
  }, [userData]);

  // Fetch department missions from the API (apply URL, saved filters, or defaults)
  const getDepartmentMissions = () => {
    ApiDatabase.getDepartmentMissions(
      { token },
      (data) => {
        if (data.departments.length > 0) {
          const source = filterSourceRef.current;
          const urlDepts = initialUrlParams.current.departments
            ? initialUrlParams.current.departments.split(',')
            : [];
          const checkedNames =
            source === 'url'
              ? urlDepts
              : source === 'localStorage'
                ? savedDepartmentsRef.current
                : [];
          const deps = data.departments.map((department) => ({
            name: department,
            checked: checkedNames.includes(department),
          }));
          setDepartments(deps);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  };

  // Mémoriser les valeurs filtrées pour éviter les re-renders infinis
  const typeContractFiltered = useMemo(
    () => typeContract.filter((item) => item.checked).map((item) => item.type),
    [typeContract]
  );
  const departmentsFiltered = useMemo(
    () => departments.filter((item) => item.checked).map((item) => item.name),
    [departments]
  );
  const missionPeriodFiltered = useMemo(
    () => missionPeriod.filter((item) => item.checked).map((item) => item.type),
    [missionPeriod]
  );
  const missionStatusFiltered = useMemo(
    () => missionStatus.filter((item) => item.checked).map((item) => item.type),
    [missionStatus]
  );
  const employeesSearchFiltered = useMemo(
    () => employeesSearch.filter((item) => item.checked).map((item) => item._id),
    [employeesSearch]
  );

  // Function to perform mission search based on filters and search input
  // Mémorisée avec useCallback pour éviter les re-renders infinis
  const manageMissionSearch = useCallback(() => {
    setSearchMissionResultWaiting(true);
    setIsInfoMissionLoadData(false);
    setIsInfoMissionWaiting(false);

    ApiDatabase.searchMission(
      {
        token,
        search,
        isMissionSearchAssigned,
        isMissionSearchSourcing,
        typeContract: typeContractFiltered,
        departments: departmentsFiltered,
        missionStatus: [
          ...missionPeriodFiltered,
          ...missionStatusFiltered,
          ...(missionStatusFiltered.includes('canceled') ? ['deleted'] : []),
        ],
        employeesSearch: employeesSearchFiltered,
        limit: limitElement,
        page: currentPage,
      },
      (data) => {
        setSearchMissionResult(data.missions);
        setCountTotal(data.count);
        setCountPerPage(data.countPerPage);
        setTotalPages(data.countPages);
        
        if (data.missions.length > 0) {
          if (pendingMissionFromUrl.current) {
            const found = data.missions.find(
              (m) => m.id_mission_real === pendingMissionFromUrl.current
            );
            setEditMission(found ? pendingMissionFromUrl.current : data.missions[0].id_mission_real);
            pendingMissionFromUrl.current = null;
          } else {
            setEditMission(data.missions[0].id_mission_real);
          }
        }
        setSearchMissionResultWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') {
          setSearchMissionResultWaiting(false);
          setSearchMissionResult([]);
          console.error(err);
        }
      }
    );
  }, [
    token,
    search,
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    typeContractFiltered,
    departmentsFiltered,
    missionPeriodFiltered,
    missionStatusFiltered,
    employeesSearchFiltered,
    limitElement,
    currentPage,
    setSearchMissionResult,
  ]);

  // Effects to trigger search when filters or pagination change (after init)
  useEffect(() => {
    if (!filtersReady) return;
    manageMissionSearch();
  }, [
    filtersReady,
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    typeContract,
    departments,
    missionPeriod,
    missionStatus,
    employeesSearch,
    currentPage,
  ]);

  // Debounce search input change to reduce API calls
  useEffect(() => {
    if (!filtersReady) return;
    const timer = setTimeout(() => {
      manageMissionSearch();
    }, 400);
    return () => clearTimeout(timer);
  }, [search, filtersReady]);

  // Reset to first page when any filter or search input changes (skip on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
  }, [
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    typeContract,
    departments,
    missionPeriod,
    missionStatus,
    employeesSearch,
    search,
  ]);

  // Sync filter state to URL params (after init, only when params actually change)
  useEffect(() => {
    if (!filtersReady) return;

    const params = buildFilterSearchParams({
      isMissionSearchAssigned,
      isMissionSearchSourcing,
      departments,
      missionPeriod,
      missionStatus,
      employeesSearch,
      search,
      editMission,
      currentPage,
    });

    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      setSearchParams(params, { replace: true });
    }
  }, [
    isMissionSearchAssigned,
    isMissionSearchSourcing,
    departments,
    missionPeriod,
    missionStatus,
    employeesSearch,
    search,
    editMission,
    currentPage,
    filtersReady,
    searchParams,
  ]);

  // Watch for mission param changes in URL (e.g., Cmd+K navigation while already on /missions)
  const prevMissionParam = useRef(searchParams.get('mission'));
  useEffect(() => {
    const missionParam = searchParams.get('mission');
    if (missionParam && missionParam !== prevMissionParam.current) {
      pendingMissionFromUrl.current = missionParam;
      setEditMission(missionParam);
    }
    prevMissionParam.current = missionParam;
  }, [searchParams]);

  // Fetch departments after filters are initialized
  useEffect(() => {
    if (!filtersReady) return;
    getDepartmentMissions();
  }, [filtersReady]);

  // Reset related search states when mission assignment filter is false
  useEffect(() => {
    if (isMissionSearchAssigned === false) {
      setIsMissionSearchSigned(false);
      setIsMissionSearchSignatureSend(false);
    }
  }, [isMissionSearchAssigned]);

  // Automatically update assignment filters based on signature status
  useEffect(() => {
    if (isMissionSearchSigned === true) {
      setIsMissionSearchAssigned(true);
      setIsMissionSearchSignatureSend(true);
    }
  }, [isMissionSearchSigned]);

  useEffect(() => {
    if (isMissionSearchSignatureSend === true) {
      setIsMissionSearchAssigned(true);
    } else if (isMissionSearchSignatureSend === false) {
      setIsMissionSearchSigned(false);
    }
  }, [isMissionSearchSignatureSend]);

  // Opens the company info slide-over panel
  const openCompanyInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_company');
  };

  // Set default search parameters based on user role (only when no URL / saved filters)
  useEffect(() => {
    if (!filtersReady) return;
    if (filterSourceRef.current !== 'default') return;
    if (role === 525) {
      setIsMissionSearchAssigned(true);
      setIsMissionSearchSignatureSend(false);
    }
  }, [role]);

  // Update the contract type filter state based on user selection
  const updateTypeContract = (updatedTypeContract) => {
    const updated = [...typeContract];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedTypeContract.length; j++) {
        if (updated[i].type === updatedTypeContract[j].value) {
          updated[i].checked = updatedTypeContract[j].checked;
        }
      }
    }
    if (updated.some((item) => item.checked)) {
      setIsMissionSearchAssigned(true);
    }
    setTypeContract(updated);
  };

  // Update the departments filter state
  const updateDepartment = (updatedDepartment) => {
    const updated = [...departments];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedDepartment.length; j++) {
        if (updated[i].name === updatedDepartment[j].value) {
          updated[i].checked = updatedDepartment[j].checked;
        }
      }
    }
    setDepartments(updated);
  };

  // Update the mission period filter state
  const updateMissionPeriod = (updatedMissionPeriod) => {
    const updated = [...missionPeriod];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedMissionPeriod.length; j++) {
        if (updated[i].type === updatedMissionPeriod[j].value) {
          updated[i].checked = updatedMissionPeriod[j].checked;
        }
      }
    }
    setMissionPeriod(updated);
  };

  // Update the mission status filter state
  const updateMissionStatus = (updatedMissionStatus) => {
    const updated = [...missionStatus];
    for (let i = 0; i < updated.length; i++) {
      for (let j = 0; j < updatedMissionStatus.length; j++) {
        if (updated[i].type === updatedMissionStatus[j].value) {
          updated[i].checked = updatedMissionStatus[j].checked;
        }
      }
    }
    setMissionStatus(updated);
  };

  // Opens the temporary worker info slide-over panel
  const openTempWorkerInfo = (id) => {
    setObjID(id);
    toggleSlideOver('get_temp_worker');
  };

  // Handles the selection of missions for bulk actions
  const handleSelectMission = (missionId, checked) => {
    setSelectedMissions((prev) => {
      if (checked) return [...prev, missionId];
      return prev.filter((id) => id !== missionId);
    });
  };

  // Fetch employees after filters are initialized
  useEffect(() => {
    if (!filtersReady) return;

    ApiDatabase.getEmployeesToAssignMission(
      { token },
      (data) => {
        if (data.employees.length > 0) {
          const source = filterSourceRef.current;
          const urlEmployeeIds = initialUrlParams.current.adminid
            ? initialUrlParams.current.adminid.split(',')
            : [];
          const checkedIds =
            source === 'url'
              ? urlEmployeeIds
              : source === 'localStorage'
                ? savedEmployeesRef.current
                : [];
          const updated = data.employees
            .map((employee) => ({
              ...employee,
              checked: checkedIds.includes(employee._id),
            }))
            .sort((a, b) => a.firstname.localeCompare(b.firstname));
          updated.push({
            firstname: 'Non assigné',
            lastname: '',
            checked: checkedIds.includes('unassign'),
            _id: 'unassign',
          });
          setEmployees(updated);
          setEmployeesSearch(updated);
        }
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [filtersReady, token]);

  // Update employee search filter state, handling special case for unassigned
  const updateEmployeeSearch = (updatedEmployeeSearch) => {
    // Clone employeesSearch array to avoid direct state mutation
    const updated = employeesSearch.map((emp) => ({ ...emp }));

    // Create a mapping object for quick access to updatedEmployeeSearch items
    const searchMap = updatedEmployeeSearch.reduce((acc, item) => {
      acc[item.value] = item;
      return acc;
    }, {});

    // Check if 'unassign' is checked in updatedEmployeeSearch
    const isUnassignInUpdatedEmployeeSearchChecked = !!(
      searchMap['unassign'] && searchMap['unassign'].checked
    );
    // Find the 'unassign' item in the cloned updated array
    const unassignItem = updated.find((item) => item._id === 'unassign');
    // Check if 'unassign' is checked in updated array
    const isUnassignInUpdatedChecked = unassignItem && unassignItem.checked;

    if (
      isUnassignInUpdatedEmployeeSearchChecked &&
      !isUnassignInUpdatedChecked
    ) {
      // If 'unassign' is checked in updatedEmployeeSearch but not in updated:
      // Check 'unassign' and uncheck all others.
      updated.forEach((item) => {
        item.checked = item._id === 'unassign';
      });
    } else if (!isUnassignInUpdatedEmployeeSearchChecked) {
      // If 'unassign' is not checked in updatedEmployeeSearch:
      // Update each item based on the mapping.
      updated.forEach((item) => {
        if (searchMap[item._id] !== undefined) {
          item.checked = searchMap[item._id].checked;
        }
      });
    } else if (
      isUnassignInUpdatedEmployeeSearchChecked &&
      isUnassignInUpdatedChecked
    ) {
      // If 'unassign' is checked in both arrays:
      // Uncheck 'unassign' in updated and update the other items.
      updated.forEach((item) => {
        if (item._id === 'unassign') {
          item.checked = false;
        } else if (searchMap[item._id] !== undefined) {
          item.checked = searchMap[item._id].checked;
        }
      });
    }

    setEmployeesSearch(updated);
  };

  // Assigns an employee to multiple selected missions
  const updateEmployee = (updatedEmployee) => {
    const selectedEmployee = updatedEmployee.find(
      (employee) => employee.checked
    );
    const newEmployeeId = selectedEmployee.value;

    ApiDatabase.postMissionAdminEmployeeAssigned(
      {
        token,
        idEmployee: newEmployeeId,
        idMissions: selectedMissions,
      },
      (data) => {
        setSearchMissionResult((prevResults) =>
          prevResults.map((mission) =>
            selectedMissions.includes(mission.id_mission_real)
              ? {
                  ...mission,
                  admin_employee_assigned:
                    newEmployeeId === 'unassign' ? null : newEmployeeId,
                }
              : mission
          )
        );
        setSelectedMissions([]);
        toast.success('Assignation effectuée');
      },
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  };

  // Assigns an employee to a single mission and updates the result list
  const updateEmployeeForMission = (missionId, updatedEmployee) => {
    const selectedEmployee = updatedEmployee.find(
      (employee) => employee.checked
    );
    const newEmployeeId = selectedEmployee.value;

    ApiDatabase.postMissionAdminEmployeeAssigned(
      {
        token,
        idEmployee: newEmployeeId,
        idMissions: [missionId],
      },
      (data) => {
        setSearchMissionResult((prevMissions) =>
          prevMissions.map((mission) =>
            mission.id_mission_real === missionId
              ? {
                  ...mission,
                  admin_employee_assigned:
                    newEmployeeId === 'unassign' ? null : newEmployeeId,
                }
              : mission
          )
        );

        toast.success('Assignation effectuée');
      },
      (err) => console.error(err)
    );
  };

  const resolveFilterState = useCallback(
    (overrides = {}) => ({
      isMissionSearchAssigned:
        'isMissionSearchAssigned' in overrides
          ? overrides.isMissionSearchAssigned
          : isMissionSearchAssigned,
      isMissionSearchSourcing:
        'isMissionSearchSourcing' in overrides
          ? overrides.isMissionSearchSourcing
          : isMissionSearchSourcing,
      typeContract: overrides.typeContract ?? typeContract,
      departments: overrides.departments ?? departments,
      missionPeriod: overrides.missionPeriod ?? missionPeriod,
      missionStatus: overrides.missionStatus ?? missionStatus,
      employeesSearch: overrides.employeesSearch ?? employeesSearch,
    }),
    [
      isMissionSearchAssigned,
      isMissionSearchSourcing,
      typeContract,
      departments,
      missionPeriod,
      missionStatus,
      employeesSearch,
    ]
  );

  const getFiltersSnapshot = useCallback(
    (overrides = {}) =>
      JSON.stringify(serializeFilters(resolveFilterState(overrides))),
    [resolveFilterState]
  );

  const getHasFilterChanges = useCallback(
    (overrides = {}) => getFiltersSnapshot(overrides) !== savedFiltersSnapshot,
    [getFiltersSnapshot, savedFiltersSnapshot]
  );

  const hasFilterChanges = getHasFilterChanges();

  const saveDefaultFilters = useCallback(
    (overrides = {}) => {
      if (!userData?._id) return;
      const filterState = resolveFilterState(overrides);
      const serialized = serializeFilters(filterState);
      localStorage.setItem(getFilterStorageKey(userData._id), JSON.stringify(serialized));
      setSavedFiltersSnapshot(toSavedSnapshot(serialized));
      setHasSavedFilters(true);
      savedDepartmentsRef.current = serialized.departments;
      savedEmployeesRef.current = serialized.employeesSearch;

      if ('isMissionSearchAssigned' in overrides) {
        setIsMissionSearchAssigned(overrides.isMissionSearchAssigned);
      }
      if ('isMissionSearchSourcing' in overrides) {
        setIsMissionSearchSourcing(overrides.isMissionSearchSourcing);
      }

      toast.success('Filtres sauvegardés');
    },
    [userData, resolveFilterState]
  );

  // Return all states and functions needed by the MissionPeriod component
  return {
    search,
    setSearch,
    searchMissionResult,
    searchMissionResultWaiting,
    isMissionSearchAssigned,
    setIsMissionSearchAssigned,
    isMissionSearchSourcing,
    setIsMissionSearchSourcing,
    typeContract,
    updateTypeContract,
    departments,
    updateDepartment,
    missionPeriod,
    updateMissionPeriod,
    missionStatus,
    updateMissionStatus,
    employees,
    employeesSearch,
    updateEmployee,
    updateEmployeeSearch,
    openCompanyInfo,
    openTempWorkerInfo,
    handleSelectMission,
    selectedMissions,
    updateEmployeeForMission,
    currentPage,
    setCurrentPage,
    totalPages,
    countPerPage,
    countTotal,
    editMission,
    setEditMission,
    hoveredCompanyItems,
    setHoveredCompanyItems,
    setSearchMissionResult,
    hasFilterChanges,
    getHasFilterChanges,
    saveDefaultFilters,
  };
}
